'use client';

import { createContext, useState, useEffect, ReactNode } from 'react';
import { ConsentManager } from '@/contexts/application/consent/consentManager';
import { ConsentPreferences, CookieCategory } from '@/contexts/application/consent/types';

interface ConsentContextValue {
  preferences: ConsentPreferences | null;
  hasGivenConsent: boolean;
  hasConsent: (category: CookieCategory) => boolean;
  updateConsent: (preferences: Partial<ConsentPreferences>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  resetConsent: () => void;
  isLoading: boolean;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

interface ConsentProviderProps {
  children: ReactNode;
}

/**
 * Consent Provider Component
 * Wraps the application with consent management context
 * Must be rendered client-side only
 */
export function ConsentProvider({ children }: ConsentProviderProps) {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize consent manager on client side only
    const manager = ConsentManager.getInstance();
    manager.initialize();

    // Get initial preferences
    setPreferences(manager.getPreferences());
    setIsLoading(false);

    // Subscribe to consent changes
    const unsubscribe = manager.onChange((event) => {
      setPreferences(event.preferences);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const hasGivenConsent = preferences !== null;

  const hasConsent = (category: CookieCategory): boolean => {
    const manager = ConsentManager.getInstance();
    return manager.hasConsent(category);
  };

  const updateConsent = (newPreferences: Partial<ConsentPreferences>): void => {
    const manager = ConsentManager.getInstance();
    manager.updateConsent(newPreferences);
  };

  const acceptAll = (): void => {
    const manager = ConsentManager.getInstance();
    manager.acceptAll();
  };

  const rejectAll = (): void => {
    const manager = ConsentManager.getInstance();
    manager.rejectAll();
  };

  const resetConsent = (): void => {
    const manager = ConsentManager.getInstance();
    manager.resetConsent();
  };

  const contextValue: ConsentContextValue = {
    preferences,
    hasGivenConsent,
    hasConsent,
    updateConsent,
    acceptAll,
    rejectAll,
    resetConsent,
    isLoading,
  };

  return (
    <ConsentContext.Provider value={contextValue}>
      {children}
    </ConsentContext.Provider>
  );
}
